package com.evoapps.evoshift.domain.model

import com.evoapps.evoshift.domain.market.payroll.WorkedSpan
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Duration.Companion.minutes
import kotlinx.serialization.Serializable

/** The shift's planned times as a span of work (SH-57); null when they cannot be read. */
fun Shift.plannedSpan(): WorkedSpan? = plannedTime.toSpan()

/**
 * The times the pay is for: clocked where clocked in and out, planned otherwise (SH-57). SH-116:
 * the clocked span ends earlier by the unpaid break under [breakPolicy] (the default policy has
 * none), so the month's hours, overtime and minimum wage count worked time only. SH-134: the
 * clocked times are the payable ones under [clockReview] (clamped to the plan when the workplace
 * counts from the planned start and no manager approved more).
 */
fun Shift.paidSpan(breakPolicy: BreakPolicy = BreakPolicy(), clockReview: ClockReviewSettings = ClockReviewSettings()): WorkedSpan? {
    val payable = payableTimes(clockReview) ?: return plannedSpan()
    // Clocked times that cannot be read count as planned; readable ones clamped to nothing count nothing.
    val clocked = payable.toSpan() ?: return if (actualTime?.toSpan() != null) null else plannedSpan()
    val unpaid = unpaidBreaks(breakPolicy, clockReview).sumOf { BreakRules.minutesBetween(it.start, it.end) }
    if (unpaid == 0L) return clocked
    val end = (clocked.end.toInstant(TimeZone.UTC) - unpaid.minutes).toLocalDateTime(TimeZone.UTC)
    return if (end > clocked.start) clocked.copy(end = end) else null
}

private fun TimeRange.toSpan(): WorkedSpan? = runCatching {
    WorkedSpan(LocalDateTime.parse(start.take(19)), LocalDateTime.parse(end.take(19)))
}.getOrNull()?.takeIf { it.end > it.start }

// domain/model/shift/Shift.kt
@Serializable
data class Shift(
    val id: String,
    val rosterId: String,
    val date: String, // ISO Date "2026-02-01"
    val dailyRecordId: String,

    val requiredRole: String?, // np. "Barista"
    val requiredSkills: List<String> = emptyList(),

    // Assignment
    val userId: String?, // Nullable for Open/Skeleton shifts
    val userDisplayName: String?, // Joined from User for UI display
    val actualUserId: String?,

    // Status & Versioning
    val isPublished: Boolean,
    val hasUnpublishedChanges: Boolean,
    val version: Int,
    /**
     * SH-271: who the team saw on this published shift before it was given to someone else since
     * the last publish ([RosterPublishing.publishedAssignee]); the next publish tells them too and
     * clears it. Null: the assignment did not change since publishing (or nobody had it).
     */
    val publishedUserId: String? = null,

    // Time
    val plannedTime: TimeRange,
    val actualTime: TimeRange?,

    // Marketplace State
    val swapRequest: SwapRequest? = null,

    /**
     * An unassigned shift a manager deliberately offers to the team. Publishing keeps it and puts
     * it on the marketplace; unassigned shifts that are not open are removed when publishing.
     * Assigning someone clears it.
     */
    val isOpen: Boolean = false,

    // Conflicts & Metadata
    val conflictMetadata: ConflictMetadata?,
    val metadata: AuditMetadata,
    val absenceId: String?,
    val tags: List<String>,
    /** How the actual times were recorded (SH-107: phone, clock-in tablet or a manager). */
    @kotlinx.serialization.Transient
    val clockAudit: ClockAudit? = null,
    /** SH-116: breaks taken while clocked in (`actual.breaks`), in order. */
    val breaks: List<ShiftBreak> = emptyList(),
    /**
     * SH-128: a manager said nobody worked this unassigned shift (a past day): it has no cost and
     * no longer asks who worked it.
     */
    val notWorked: Boolean = false,
    /** SH-134: flags on the clock times, the manager's decision and the tablet photos. */
    @kotlinx.serialization.Transient
    val clockCheck: ClockCheck? = null,
    /** SH-134: an unplanned clock-in on this shift (someone covering, or a new shift). */
    @kotlinx.serialization.Transient
    val unplanned: UnplannedClock? = null,
    /**
     * SH-236: why nobody came to an assigned shift ([notWorked] set by a manager), optional and
     * short ([ActualWork.MAX_NOTE]). Older apps do not know it and skip it.
     */
    val noShowReason: String? = null,
    /**
     * SH-236: this shift is one part of a shift divided at a handover (someone fell ill and a
     * colleague took over). Both parts are ordinary shifts; this only links them for the audit
     * and "Undo split". Older apps do not know it and show both parts as they are.
     */
    val split: ShiftSplit? = null,
    /**
     * SH-267: the planned person ([userId]) is borrowed from this other workplace of the same
     * workplace group: they have no engagement here, keep their home pay terms, and the shift's
     * cost snapshot is priced on the server (functions/borrowing.js). Null: one of the workplace's
     * own people (or nobody). Older apps do not know it; the server puts it back if they drop it.
     */
    val borrowedFromWorkplaceId: String? = null
)

/** SH-267: the planned person is borrowed from another workplace of the group ([Shift.borrowedFromWorkplaceId]). */
val Shift.isBorrowed: Boolean get() = borrowedFromWorkplaceId != null && userId != null

/**
 * SH-267: the shift's cost snapshot is the server's: borrowed and worked by the borrowed person
 * (their home pay terms are not the workplace's to read). The app neither prices nor removes it.
 */
val Shift.pricedOnServer: Boolean get() = isBorrowed && workerId == userId

/**
 * SH-128: who worked (and is paid for) the shift: the person recorded afterwards
 * ([Shift.actualUserId], e.g. on an open shift somebody covered), else the assignee. SH-236:
 * nobody when a manager recorded that nobody came ([Shift.notWorked]): the shift then has no
 * cost, no hours and is in nobody's own hours or earnings.
 */
val Shift.workerId: String?
    get() = if (notWorked) null else actualUserId ?: userId

/** SH-236: someone was assigned and a manager recorded that they did not come (and nobody else did). */
val Shift.isNoShow: Boolean
    get() = notWorked && userId != null

/**
 * SH-136: a person's own schedule (My shifts, the dashboard's next shift, the widget, the watch,
 * the calendar feed): the shifts they work, [workerId] == [userId]. That is the shifts assigned
 * to them ([assigned], queried by `userId`) less those somebody else was recorded on, plus the
 * shifts they were recorded on afterwards ([worked], queried by `actualUserId`: an open shift they
 * covered, or a colleague's shift whose cover a manager confirmed). Each shift once.
 */
fun ownSchedule(assigned: List<Shift>, worked: List<Shift>, userId: String): List<Shift> =
    (assigned + worked).distinctBy { it.id }.filter { it.workerId == userId }

/**
 * SH-128: an open shift on [today] or before that nobody was assigned to and nobody has been
 * recorded on yet: the day's panel asks who worked it.
 */
fun Shift.asksWhoWorked(today: String): Boolean =
    userId == null && actualUserId == null && !notWorked && date.isNotEmpty() && date <= today

@Serializable
data class TimeRange(
    val start: String, // ISO Timestamp
    val end: String
)

@Serializable
data class ConflictMetadata(
    val isForced: Boolean,
    val conflictType: ConflictType,
    val conflictingShiftId: String?
)

@Serializable
enum class ConflictType {
    DOUBLE_BOOKED, ABSENCE_OVERLAP, REST_VIOLATION, UNAVAILABLE,
    /** The medical exam or OHS training has expired by the shift's day (SH-47; never stored on a shift). */
    EXPIRED_STAFF_CHECK
}

/**
 * Another shift of the same person overlapping a shift being assigned, in any workplace. Comes from
 * the findShiftConflicts Cloud Function: for another owner's workplace only the times are known.
 */
@Serializable
data class ShiftOverlap(
    val shiftId: String,
    val plannedTime: TimeRange,
    val sameOwner: Boolean,
    val workplaceName: String?
)

/**
 * SH-135: a person's shift at another of their workplaces, as the planner of this workplace may
 * know it (the findBusyElsewhere Cloud Function): the same owner's workplaces with their name,
 * drafts included; another employer's only once published, and only the times.
 */
@Serializable
data class BusyElsewhere(
    val userId: String,
    val date: String,
    val plannedTime: TimeRange,
    val sameOwner: Boolean,
    /** The workplace's name for the same owner; never another employer's. */
    val workplaceName: String?
)
